import React, { useEffect, useState } from "react";

import { Error } from "@mui/icons-material";
import {
  Alert,
  Box,
  DialogContent,
  Grid,
  InputAdornment,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { UserSortType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import ConfirmDialog from "../shared/confirm-dialog";
import { GetUsersListParams, getOrgUserList } from "api/endpoints/organisation";
import { updateEmail } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";

interface UpdateOrgEmailDialogProps {
  roleId: string;
  regionCode: string;
  open: boolean;
  setOpen: (newOpen: boolean) => void;
}

interface emailAndUserId {
  userId: string;
  emailAddress: string;
}

const UpdateOrgEmailDialog = ({
  roleId,
  regionCode,
  open,
  setOpen,
}: UpdateOrgEmailDialogProps) => {
  const theme = useTheme();
  const [searchDomain, setSearchDomain] = useState<string>("");
  const [newDomain, setNewDomain] = useState<string>("");
  const [successUpdateEmailOpen, setSuccessUpdateEmailOpen] = useState(false);
  const [warningUpdateEmailOpen, setWarningUpdateEmailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(0);
  const [progress, setProgress] = useState(0);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchResults, setSearchResults] = useState<emailAndUserId[]>([]);
  const [emailValidationError, setEmailValidationError] = useState(false);

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchDomain("");
      setNewDomain("");
      setSearchResults([]);
      setLoading(false);
      setTotalSearchResults(0);
      setProgress(0);
      setWarning(0);
      setEmailValidationError(false);
      setSuccessUpdateEmailOpen(false);
      setWarningUpdateEmailOpen(false);
    }
  }, [open]);

  const handleUpdatedDomainInput = (value: string, key: string) => {
    if (key === "searchDomain") {
      setSearchDomain(value);
      domainValidation(value);
    } else if (key === "updatedDomain") {
      setNewDomain(value);
      domainValidation(value);
    }
  };

  const searchUsersWithDomain = async (
    regionCode: string,
    orgId: string,
    offset: number,
    userQuery: string,
  ) => {
    setLoading(true);
    const payload: GetUsersListParams = {
      organizationId: orgId,
      pageSize: 100,
      offset: offset,
      sortDirection: SortDirection.ASC,
      sortType: UserSortType.FIRST_NAME,
      adminOnly: false,
      query: userQuery,
      userStatus: 0,
      includeInactiveRoles: true,
    };
    const resSearchResults = await getOrgUserList(
      payload,
      callOptionsHeaders(regionCode, roleId),
    );
    await setTotalSearchResults(resSearchResults.totalItems);
    const resultsIdsEmails = [];
    for (const index in resSearchResults.users) {
      resultsIdsEmails.push({
        userId: resSearchResults.users[index].id,
        emailAddress: resSearchResults.users[index].email,
      });
    }
    return {
      totalSearchResults: resSearchResults.totalItems,
      searchResults: resultsIdsEmails,
    };
  };

  const updateEmails = async (regionCode: string) => {
    let count = 0;
    for (const index in searchResults) {
      await updateEmail(
        {
          userId: searchResults[index].userId,
          email:
            searchResults[index].emailAddress.split("@")[0] + "@" + newDomain,
        },
        callOptionsHeaders(regionCode, searchResults[index].userId),
      ).catch(() => {
        setLoading(true);
        setWarning(warning + 1);
      });
      setLoading(true);
      setTotalSearchResults(totalSearchResults - 1);
      count += 1;
      setProgress((count / totalSearchResults) * 100);
    }
  };

  const handleUpdateEmail = async () => {
    setLoading(true);
    await updateEmails(regionCode);
    if (warning === 0) {
      setSuccessUpdateEmailOpen(true);
    } else {
      setWarningUpdateEmailOpen(true);
    }
    setSearchDomain("");
    setNewDomain("");
    setLoading(false);
    setSearchResults([]);
  };

  const handleSearchEmail = async (offset: number) => {
    setLoading(true);

    let search = await searchUsersWithDomain(
      regionCode,
      roleId,
      offset,
      searchDomain,
    );

    let searchResults = search.searchResults;
    for (let offset = 100; offset <= search.totalSearchResults; offset += 100) {
      search = await searchUsersWithDomain(
        regionCode,
        roleId,
        offset,
        searchDomain,
      );
      searchResults = [...searchResults, ...search.searchResults];
    }
    setSearchResults(searchResults);
    setLoading(false);
  };

  const domainValidation = (domain: string) => {
    if (/^[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(domain)) {
      setEmailValidationError(false);
    } else {
      setEmailValidationError(true);
    }
  };

  useEffect(() => {
    domainValidation(newDomain);
    if (warning != 0) {
      setWarningUpdateEmailOpen(true);
    }
  }, [newDomain, warning]);

  return (
    <>
      <ConfirmDialog
        title="Update Domains"
        disableBackgroundClick
        open={open}
        disabled={
          loading ||
          emailValidationError ||
          searchDomain.trim() === "" ||
          (totalSearchResults > 0 && newDomain == "")
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel={searchResults.length > 0 ? "Update" : "Search"}
        setOpen={setOpen}
        disableCloseOnConfirm={true}
        onCancel={async () => {
          setSearchDomain("");
          setNewDomain("");
          setSearchResults([]);
          setLoading(false);
          setTotalSearchResults(0);
        }}
        onConfirm={async () => {
          setLoading(true);
          if (searchResults.length > 0) {
            await handleUpdateEmail();
            setOpen(false);
          } else {
            await handleSearchEmail(0);
          }
        }}
      >
        <DialogContent style={{ paddingBottom: "0" }}>
          <Grid
            item
            sm={12}
            container
            direction="column"
            justifyContent="center"
          >
            <Box
              sx={{ width: 1 }}
              bgcolor={theme.palette.warning.main}
              borderRadius="5px"
              border={1}
              padding={1}
              marginBottom={2}
              borderColor={theme.palette.secondary.main}
            >
              <Grid container alignItems="center">
                <Grid item xs={2} container justifyContent="center">
                  <Error fontSize="large" />
                </Grid>
                <Grid item xs={10} justifyContent="center">
                  This action does not trigger confirmation emails. <br />
                  Do not navigate away from this page once the workflow is
                  running.
                </Grid>
              </Grid>
            </Box>
            <Typography
              variant="body2"
              display="inline"
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
            >
              Enter the domain you want to replace. eg safetyculture.io
            </Typography>
          </Grid>
          <TextField
            margin="normal"
            id="search-users-by-domain"
            label="Search Current Domain"
            value={searchDomain}
            disabled={totalSearchResults > 0 || loading}
            onChange={(e) =>
              handleUpdatedDomainInput(e.target.value, "searchDomain")
            }
            variant="outlined"
            size="small"
            style={{ width: "100%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">@</InputAdornment>
              ),
            }}
            inputProps={{ "data-testid": "search-users-by-domain" }}
          />
          {loading && <LinearProgress style={{ width: "100%" }} />}
          {searchResults.length > 0 ? (
            <Grid>
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                {`There were ${searchResults.length} user emails found matching ${searchDomain}`}
                {/* if searchResults.length is >= 100 then raise a warning */}
                {searchResults.length >= 100 && (
                  <Box
                    sx={{ width: 1 }}
                    bgcolor={theme.palette.warning.main}
                    borderRadius="5px"
                    border={1}
                    padding={1}
                    marginBottom={2}
                    borderColor={theme.palette.secondary.main}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={2} container justifyContent="center">
                        <Error fontSize="large" />
                      </Grid>
                      <Grid item xs={10} justifyContent="center">
                        Over 100 users were found with the domain you entered.{" "}
                        <br />
                        This may take a while to process and could cause errors{" "}
                        <br />
                        Please reach out to Business Systems before proceeding
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {}
                {`Enter the new domain below to replace ${searchDomain}`}
              </Typography>
              <TextField
                margin="normal"
                id="new-domain-input"
                label="New Domain"
                disabled={loading}
                value={newDomain}
                onChange={(e) =>
                  handleUpdatedDomainInput(e.target.value, "updatedDomain")
                }
                variant="outlined"
                size="small"
                style={{ width: "100%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">@</InputAdornment>
                  ),
                }}
                inputProps={{ "data-testid": "new-domain-input" }}
              />
              <Grid style={{ display: "flex", justifyContent: "center" }}>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress variant="determinate" value={progress} />

                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {progress > 0 && (
                      <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                      >{`${Math.round(progress)}%`}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
        </DialogContent>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={warning === 0 && successUpdateEmailOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessUpdateEmailOpen(false)}
        data-testid="update-domain-dialog-success"
      >
        <Alert severity="success" variant="filled">
          Successfully updated all domains
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={warningUpdateEmailOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setWarningUpdateEmailOpen(false)}
        data-testid="update-domain-dialog-warning"
      >
        <Alert severity="warning" variant="filled">
          {warning} emails were not updated
        </Alert>
      </Snackbar>
    </>
  );
};
export default UpdateOrgEmailDialog;
